import { Settings } from '@streamr/tv-engine';
import { executeSoon } from '@streamr/tv-utils';
import { EventEmitter } from 'eventemitter3';
import { DefaultVideoConstraints } from '../../consts';
import { ChosenMediaStore } from './ChosenMediaStore';
import { MediaDeviceList } from './MediaDeviceList';
import { MediaServiceOutput } from './MediaServiceOutput';
export class MediaService extends EventEmitter {
    // Singleton pattern implementation
    static instance;
    // Return the full singleton instance of the MediaService. Do not use this
    // method in React components.
    static getInstance() {
        if (!MediaService.instance) {
            MediaService.instance = new MediaService();
        }
        return MediaService.instance;
    }
    /**
     * @returns A notification interface that contains only void-returning methods
     * to be used in React components.
     */
    static getNotificationInterface() {
        return MediaService.getInstance();
    }
    /**
     * @returns A set of reactive getters to be used in React components.
     */
    static getOutput() {
        return MediaServiceOutput.getReactiveOutput();
    }
    // ------------- Singleton implementation end
    output;
    outputMediaStream;
    permissionedAudioDevices;
    permissionedVideoDevices;
    connectedVideoDevices;
    connectedAudioDevices;
    audioStream;
    videoStream;
    displayMediaStream;
    audioMuted = false;
    videoMuted = false;
    captureStarted = false;
    videoWidth = 1280;
    videoHeight = 720;
    additionalStreams = new Map();
    constructor() {
        super();
        this.output = new MediaServiceOutput();
        this.permissionedAudioDevices = new MediaDeviceList();
        this.permissionedVideoDevices = new MediaDeviceList();
        this.connectedVideoDevices = new MediaDeviceList();
        this.connectedAudioDevices = new MediaDeviceList();
        // Listen to device add/remove events
        if (navigator.mediaDevices) {
            navigator.mediaDevices.addEventListener('devicechange', () => {
                this.updatePermissionedDevices().catch((error) => {
                    console.error('Error updating devices', error);
                });
                this.updateConnectedMediaDevices().catch((error) => {
                    console.error('Error updating connected devices', error);
                });
            });
        }
        this.updatePermissionedDevices().catch((error) => {
            console.error('Error initializing permissions', error);
        });
        this.updateConnectedMediaDevices().catch((error) => {
            console.error('Error updating connected devices', error);
        });
    }
    async init() {
        // ToDo: Implement initialization
    }
    getChosenAudioDevice() {
        const chosenAudioDevice = ChosenMediaStore.getInstance().getChosenAudioDevice();
        if (chosenAudioDevice &&
            this.permissionedAudioDevices.has(chosenAudioDevice)) {
            return chosenAudioDevice;
        }
        if (this.permissionedAudioDevices.getDevices().length > 0) {
            const device = this.permissionedAudioDevices.getDevices()[0];
            ChosenMediaStore.getInstance().setChosenAudioDevice(device);
            return device;
        }
        return undefined;
    }
    getChosenVideoDevice() {
        const chosenVideoDevice = ChosenMediaStore.getInstance().getChosenVideoDevice();
        if (chosenVideoDevice &&
            this.permissionedVideoDevices.has(chosenVideoDevice)) {
            return chosenVideoDevice;
        }
        if (this.permissionedVideoDevices.getDevices().length > 0) {
            const device = this.permissionedVideoDevices.getDevices()[0];
            ChosenMediaStore.getInstance().setChosenVideoDevice(device);
            return device;
        }
        return undefined;
    }
    async updatePermissionedDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        // If a device has no label, we do not have permission to use it,
        // so we filter them out
        const audioDevices = devices.filter((device) => {
            if (device.kind === 'audioinput') {
                return device.label && device.label.length > 0;
            }
            return false;
        });
        const videoDevices = devices.filter((device) => {
            if (device.kind === 'videoinput') {
                return device.label && device.label.length > 0;
            }
            return false;
        });
        // Update the devices in the output state if they have changed
        const audioDevicesChanged = this.permissionedAudioDevices.updateDevices(audioDevices);
        if (audioDevicesChanged) {
            if (this.permissionedAudioDevices.getDevices().length > 0) {
                this.output.setMicrophonePermissioned(true);
            }
            else {
                this.output.setMicrophonePermissioned(false);
            }
            this.output.setAudioDevices(this.permissionedAudioDevices.getDevices());
        }
        const videoDevicesChanged = this.permissionedVideoDevices.updateDevices(videoDevices);
        if (videoDevicesChanged) {
            if (this.permissionedVideoDevices.getDevices().length > 0) {
                this.output.setCameraPermissioned(true);
            }
            else {
                this.output.setCameraPermissioned(false);
            }
            this.output.setVideoDevices(this.permissionedVideoDevices.getDevices());
        }
        // If the devices have changed, update the media streams
        if (audioDevicesChanged) {
            await this.updateAudioStream();
        }
        if (videoDevicesChanged) {
            await this.updateVideoStream();
        }
    }
    async updateConnectedMediaDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter((device) => device.kind === 'audioinput');
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        this.connectedAudioDevices.updateDevices(audioDevices);
        this.connectedVideoDevices.updateDevices(videoDevices);
        this.output.setConnectedAudioDevices(this.connectedAudioDevices.getDevices());
        this.output.setConnectedVideoDevices(this.connectedVideoDevices.getDevices());
    }
    async requestMediaPermissions({ audio = false, video = false, }) {
        let mediaStream;
        try {
            mediaStream = await navigator.mediaDevices.getUserMedia({
                audio,
                video: video ? DefaultVideoConstraints : false,
            });
        }
        catch (e) {
            throw e;
        }
        for (const track of mediaStream.getTracks()) {
            track.stop();
        }
        await this.updatePermissionedDevices();
        // await this.updateMediaStream();
    }
    publishMediaStream() {
        const tracks = [];
        // Check if display media tracks are still active (not ended by browser's stop sharing)
        const activeDisplayAudioTracks = this.displayMediaStream
            ?.getAudioTracks()
            .filter((track) => track.readyState === 'live') || [];
        const activeDisplayVideoTracks = this.displayMediaStream
            ?.getVideoTracks()
            .filter((track) => track.readyState === 'live') || [];
        // If no active display tracks, clear the display media stream
        if (this.displayMediaStream &&
            activeDisplayAudioTracks.length === 0 &&
            activeDisplayVideoTracks.length === 0) {
            this.displayMediaStream = undefined;
        }
        // Prioritize active display media stream video tracks
        if (activeDisplayVideoTracks.length > 0) {
            tracks.push(...activeDisplayVideoTracks);
        }
        else if (this.videoStream) {
            tracks.push(...this.videoStream.getVideoTracks());
        }
        // Prioritize active display media stream audio tracks
        if (activeDisplayAudioTracks.length > 0) {
            tracks.push(...activeDisplayAudioTracks);
        }
        else if (this.audioStream) {
            tracks.push(...this.audioStream.getAudioTracks());
        }
        this.outputMediaStream = new MediaStream(tracks);
        const videoTrack = this.outputMediaStream.getVideoTracks()[0];
        const isUserFacingVideo = videoTrack &&
            (videoTrack.getSettings().facingMode === 'user' ||
                /facetime hd camera|integrated/i.test(videoTrack.label));
        this.output.setMediaStreamData({
            mediaStream: this.outputMediaStream,
            isUserFacingVideo: isUserFacingVideo,
            isSharingScreen: this.displayMediaStream !== undefined,
            // Check active display media tracks, not just their existence
            isSharingScreenAudio: activeDisplayAudioTracks.length > 0,
            isSharingScreenVideo: activeDisplayVideoTracks.length > 0,
            hasAudio: this.outputMediaStream.getAudioTracks().length > 0,
            hasVideo: this.outputMediaStream.getVideoTracks().length > 0,
            hasTracks: this.outputMediaStream.getTracks().length > 0,
        });
        this.emit('mediaStreamChanged');
    }
    async updateAudioStream() {
        const chosenAudioDevice = this.getChosenAudioDevice();
        if (chosenAudioDevice) {
            this.output.setSelectedAudioDevice(chosenAudioDevice);
        }
        if (!this.captureStarted) {
            return;
        }
        // Stop existing audio tracks
        this.audioStream?.getAudioTracks().forEach((track) => track.stop());
        this.audioStream = undefined;
        if (!this.audioMuted && chosenAudioDevice) {
            const tempAudioStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: {
                        exact: chosenAudioDevice.deviceId,
                    },
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: Settings.audioSampleRate,
                },
                video: false,
            });
            // Check if capture wasn't stopped in the meantime
            if (!this.captureStarted) {
                tempAudioStream.getTracks().forEach((track) => track.stop());
                return;
            }
            this.audioStream = tempAudioStream;
        }
        this.publishMediaStream();
    }
    async updateVideoStream() {
        const chosenVideoDevice = this.getChosenVideoDevice();
        if (chosenVideoDevice) {
            this.output.setSelectedVideoDevice(chosenVideoDevice);
        }
        if (!this.captureStarted) {
            return;
        }
        // Stop existing video tracks
        this.videoStream?.getVideoTracks().forEach((track) => track.stop());
        this.videoStream = undefined;
        if (!this.videoMuted && chosenVideoDevice) {
            const tempVideoStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                    ...DefaultVideoConstraints,
                    width: { ideal: this.videoWidth, max: this.videoWidth },
                    height: { ideal: this.videoHeight, max: this.videoHeight },
                    deviceId: {
                        exact: chosenVideoDevice.deviceId,
                    },
                },
            });
            // Check if capture wasn't stopped in the meantime
            if (!this.captureStarted) {
                tempVideoStream.getTracks().forEach((track) => track.stop());
                return;
            }
            this.videoStream = tempVideoStream;
            const actualTrack = tempVideoStream.getVideoTracks()[0];
            if (actualTrack) {
                const s = actualTrack.getSettings();
                console.log('[MediaService] camera resolution', {
                    requested: `${this.videoWidth}x${this.videoHeight}`,
                    actual: `${s.width}x${s.height}`,
                });
            }
        }
        this.publishMediaStream();
    }
    async updateMediaStreams() {
        await Promise.all([this.updateAudioStream(), this.updateVideoStream()]);
    }
    async updateDisplayMediaStream(surfaceType) {
        const newDisplayMediaStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                displaySurface: surfaceType,
                frameRate: {
                    ideal: Settings.videoEncoderDefaultFrameRate,
                },
            },
            audio: {
                sampleRate: Settings.audioSampleRate,
                echoCancellation: true,
                noiseSuppression: true,
                suppressLocalAudioPlayback: true,
            },
        });
        // Stop camera video when screen sharing
        this.videoStream?.getVideoTracks().forEach((track) => {
            track.stop();
        });
        this.videoStream = undefined;
        this.displayMediaStream = newDisplayMediaStream;
        for (const track of this.displayMediaStream.getTracks()) {
            track.isScreenShare = true;
            // Listen for track ended events (browser's stop sharing button)
            track.addEventListener('ended', () => {
                // Restart camera when screen share ends
                this.updateVideoStream()
                    .then(() => {
                    this.publishMediaStream();
                })
                    .catch((e) => {
                    console.error('Error restarting camera after screen share ended', e);
                });
            });
        }
        if (this.displayMediaStream.getVideoTracks().length > 0) {
            const videoTrack = this.displayMediaStream.getVideoTracks()[0];
            videoTrack.applyConstraints({
                frameRate: {
                    min: Settings.videoEncoderDefaultFrameRate,
                },
            });
        }
        this.publishMediaStream();
    }
    // Getter for other services
    getOutputMediaStream() {
        return this.outputMediaStream;
    }
    /**
     * Get the video track directly from its source stream.
     * This is needed for Safari where a track must be transferred from its original stream.
     */
    getSourceVideoTrack() {
        // Prioritize display media video tracks
        const displayVideoTrack = this.displayMediaStream
            ?.getVideoTracks()
            .find((track) => track.readyState === 'live');
        if (displayVideoTrack) {
            return displayVideoTrack;
        }
        return this.videoStream
            ?.getVideoTracks()
            .find((track) => track.readyState === 'live');
    }
    /**
     * Get the audio track directly from its source stream.
     * This is needed for Safari where a track must be transferred from its original stream.
     */
    getSourceAudioTrack() {
        // Prioritize display media audio tracks
        const displayAudioTrack = this.displayMediaStream
            ?.getAudioTracks()
            .find((track) => track.readyState === 'live');
        if (displayAudioTrack) {
            return displayAudioTrack;
        }
        return this.audioStream?.getAudioTracks()[0];
    }
    // Notifications to be called by React components
    notifyAudioDeviceSelected(audioDevice) {
        if (ChosenMediaStore.getInstance().getChosenAudioDevice() !==
            audioDevice) {
            ChosenMediaStore.getInstance().setChosenAudioDevice(audioDevice);
            this.updateAudioStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyVideoDeviceSelected(videoDevice) {
        if (ChosenMediaStore.getInstance().getChosenVideoDevice() !==
            videoDevice) {
            ChosenMediaStore.getInstance().setChosenVideoDevice(videoDevice);
            this.updateVideoStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyMediaPermissionsRequested({ audio = false, video = false, }) {
        this.requestMediaPermissions({ audio, video }).catch((e) => {
            console.error(e);
        });
    }
    notifyAudioMutingChangeRequested(muted) {
        if (this.audioMuted !== muted) {
            this.audioMuted = muted;
            this.output.setAudioMuted(muted);
            // Instead of recreating the stream, just enable/disable the existing audio track
            const audioTracks = this.audioStream?.getAudioTracks() ?? [];
            for (const track of audioTracks) {
                track.enabled = !muted;
            }
        }
    }
    notifyVideoMutingChangeRequested(muted) {
        if (this.videoMuted !== muted) {
            this.videoMuted = muted;
            this.output.setVideoMuted(muted);
            this.updateVideoStream().catch((e) => {
                console.error(e);
            });
        }
    }
    setVideoResolution(width, height) {
        if (this.videoWidth === width && this.videoHeight === height) {
            return;
        }
        this.videoWidth = width;
        this.videoHeight = height;
        if (this.captureStarted) {
            this.updateVideoStream().catch(console.error);
        }
    }
    static setAudioSessionType(type) {
        const nav = navigator;
        if (nav.audioSession) {
            nav.audioSession.type = type;
        }
    }
    notifyStartCaptureRequested() {
        if (this.captureStarted) {
            return;
        }
        MediaService.setAudioSessionType('play-and-record');
        this.captureStarted = true;
        executeSoon(() => this.updateMediaStreams());
    }
    notifyStopCaptureRequested() {
        this.captureStarted = false;
        MediaService.setAudioSessionType('playback');
        this.audioStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.audioStream = undefined;
        this.videoStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.videoStream = undefined;
        this.displayMediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.displayMediaStream = undefined;
        for (const [trackId, stream] of this.additionalStreams) {
            stream.getTracks().forEach((track) => track.stop());
            this.emit('additionalTrackRemoved', trackId);
        }
        this.additionalStreams.clear();
        this.outputMediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.outputMediaStream = undefined;
        this.publishMediaStream();
    }
    notifyScreenShareRequested(surfaceType) {
        this.updateDisplayMediaStream(surfaceType).catch((e) => {
            console.error(e);
        });
    }
    notifyScreenShareStopped() {
        this.displayMediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.displayMediaStream = undefined;
        // Restart camera when screen share is manually stopped
        this.updateVideoStream().catch((e) => {
            console.error('Error restarting camera after stopping screen share', e);
        });
    }
    notifyAddCamera(deviceId) {
        this.addAdditionalCamera(deviceId).catch((e) => {
            console.error('Error adding camera', e);
        });
    }
    notifyAddMicrophone(deviceId) {
        this.addAdditionalMicrophone(deviceId).catch((e) => {
            console.error('Error adding microphone', e);
        });
    }
    notifyShareScreen() {
        this.addScreenShare().catch((e) => {
            console.error('Error starting screen share', e);
        });
    }
    notifyRemoveLocalTrack(trackId) {
        const stream = this.additionalStreams.get(trackId);
        if (stream) {
            stream.getTracks().forEach((t) => t.stop());
            this.additionalStreams.delete(trackId);
            this.emit('additionalTrackRemoved', trackId);
            return;
        }
        // Also check display media tracks
        if (this.displayMediaStream) {
            const displayTrack = this.displayMediaStream
                .getTracks()
                .find((t) => t.id === trackId);
            if (displayTrack) {
                displayTrack.stop();
                const remaining = this.displayMediaStream
                    .getTracks()
                    .filter((t) => t.readyState === 'live');
                if (remaining.length === 0) {
                    this.displayMediaStream = undefined;
                }
                this.emit('additionalTrackRemoved', trackId);
            }
        }
    }
    async addAdditionalCamera(deviceId) {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
                ...DefaultVideoConstraints,
                width: {
                    ideal: Settings.cameraVideoWidth,
                    max: Settings.cameraVideoWidth,
                },
                height: {
                    ideal: Settings.cameraVideoHeight,
                    max: Settings.cameraVideoHeight,
                },
                deviceId: { exact: deviceId },
            },
        });
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack) {
            return;
        }
        this.additionalStreams.set(videoTrack.id, stream);
        videoTrack.addEventListener('ended', () => {
            this.additionalStreams.delete(videoTrack.id);
            this.emit('additionalTrackRemoved', videoTrack.id);
        });
        this.emit('additionalTrackAdded', videoTrack);
    }
    async addAdditionalMicrophone(deviceId) {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: { exact: deviceId },
                echoCancellation: true,
                noiseSuppression: true,
                sampleRate: Settings.audioSampleRate,
            },
            video: false,
        });
        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack) {
            return;
        }
        this.additionalStreams.set(audioTrack.id, stream);
        audioTrack.addEventListener('ended', () => {
            this.additionalStreams.delete(audioTrack.id);
            this.emit('additionalTrackRemoved', audioTrack.id);
        });
        this.emit('additionalTrackAdded', audioTrack);
    }
    async addScreenShare() {
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                frameRate: {
                    ideal: Settings.videoEncoderDefaultFrameRate,
                },
            },
            audio: {
                sampleRate: Settings.audioSampleRate,
                echoCancellation: true,
                noiseSuppression: true,
                suppressLocalAudioPlayback: true,
            },
        });
        for (const track of displayStream.getTracks()) {
            track.isScreenShare = true;
            this.additionalStreams.set(track.id, displayStream);
            track.addEventListener('ended', () => {
                this.additionalStreams.delete(track.id);
                this.emit('additionalTrackRemoved', track.id);
            });
            this.emit('additionalTrackAdded', track);
        }
    }
}
